/**
 * @sazeo/sdk — Messages hooks
 *
 * TanStack Query wrappers for the Messages module (channels, messages, reactions,
 * channel members). All calls use the user JWT from packages/auth/. No service_role.
 *
 * Usage:
 *   const { data } = useChannels(supabase, tenantId);
 *   const send = useSendMessage(supabase, tenantId);
 */

import type {
  Channel,
  ChannelCreateInput,
  ChannelMember,
  ChannelMemberAddInput,
  ChannelUpdateInput,
  Message,
  MessageCreateInput,
  MessageUpdateInput,
  PaginatedMessages,
  Reaction,
  ReactionToggleInput,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// Re-export for consumers who import only from '@sazeo/sdk'
export type { SupabaseClientLike };

// ---------------------------------------------------------------------------
// Augmented types returned by Edge Function (includes joined fields)
// ---------------------------------------------------------------------------

export interface ChannelWithMeta extends Channel {
  member_count?: number;
  unread_count?: number;
}

export interface ChannelDetail extends Channel {
  members: ChannelMember[];
}

export interface MessageWithSender extends Message {
  sender?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  reactions?: Array<{ emoji: string; count: number; user_ids: string[] }>;
  reply_count?: number;
}

// ---------------------------------------------------------------------------
// Pagination options
// ---------------------------------------------------------------------------

export interface MessageListOptions {
  /** ISO-8601 cursor — return messages created before this timestamp. */
  before?: string;
  /** Max 50. */
  limit?: number;
  /** Filter to thread replies only. */
  parentId?: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const messagesKeys = {
  all: ['messages'] as const,
  channels: (tenantId: string) => [...messagesKeys.all, 'channels', tenantId] as const,
  channel: (tenantId: string, channelId: string) =>
    [...messagesKeys.all, 'channel', tenantId, channelId] as const,
  channelMembers: (tenantId: string, channelId: string) =>
    [...messagesKeys.all, 'channelMembers', tenantId, channelId] as const,
  channelMessages: (tenantId: string, channelId: string) =>
    [...messagesKeys.all, 'msgs', tenantId, channelId] as const,
  messages: (tenantId: string, channelId: string, opts?: MessageListOptions) =>
    [...messagesKeys.channelMessages(tenantId, channelId), opts ?? {}] as const,
  mentions: (tenantId: string) => [...messagesKeys.all, 'mentions', tenantId] as const,
  reactions: (tenantId: string, messageId: string) =>
    [...messagesKeys.all, 'reactions', tenantId, messageId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

async function apiDelete(supabase: SupabaseClientLike, path: string): Promise<void> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
}

// ---------------------------------------------------------------------------
// Channel hooks
// ---------------------------------------------------------------------------

/** List all channels visible to the current user (DMs + joined public + private). */
export function useChannels(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: messagesKeys.channels(tenantId),
    queryFn: () => apiFetch<ChannelWithMeta[]>(supabase, 'messages/channels'),
    enabled: Boolean(tenantId),
  });
}

/** Single channel with member list. */
export function useChannel(supabase: SupabaseClientLike, tenantId: string, channelId: string) {
  return useQuery({
    queryKey: messagesKeys.channel(tenantId, channelId),
    queryFn: () => apiFetch<ChannelDetail>(supabase, `messages/channels/${channelId}`),
    enabled: Boolean(tenantId) && Boolean(channelId),
  });
}

/** Create a new channel (dm / group / channel). */
export function useCreateChannel(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChannelCreateInput) =>
      apiFetch<Channel>(supabase, 'messages/channels', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagesKeys.channels(tenantId) });
    },
  });
}

/** Update channel (name, description, is_private). Admin only (enforced server-side). */
export function useUpdateChannel(
  supabase: SupabaseClientLike,
  tenantId: string,
  channelId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChannelUpdateInput) =>
      apiFetch<Channel>(supabase, `messages/channels/${channelId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagesKeys.channels(tenantId) });
      void qc.invalidateQueries({ queryKey: messagesKeys.channel(tenantId, channelId) });
    },
  });
}

/** Archive a channel (sets archived_at). Admin only. */
export function useArchiveChannel(
  supabase: SupabaseClientLike,
  tenantId: string,
  channelId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ archived: boolean }>(supabase, `messages/channels/${channelId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagesKeys.channels(tenantId) });
      void qc.invalidateQueries({ queryKey: messagesKeys.channel(tenantId, channelId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Message hooks
// ---------------------------------------------------------------------------

/** Paginated messages for a channel (newest-first, cursor-based). Max 50 per page. */
export function useMessages(
  supabase: SupabaseClientLike,
  tenantId: string,
  channelId: string,
  opts: MessageListOptions = {},
) {
  return useQuery({
    queryKey: messagesKeys.messages(tenantId, channelId, opts),
    queryFn: (): Promise<PaginatedMessages> => {
      const params = new URLSearchParams({
        limit: String(Math.min(opts.limit ?? 50, 50)),
        ...(opts.before ? { before: opts.before } : {}),
        ...(opts.parentId ? { parent_id: opts.parentId } : {}),
      });
      return apiFetch<PaginatedMessages>(
        supabase,
        `messages/channels/${channelId}/messages?${params.toString()}`,
      );
    },
    enabled: Boolean(tenantId) && Boolean(channelId),
  });
}

/**
 * Send a message to a channel. The Edge Function parses @mentions and
 * detects body_lang using packages/translation detectLanguage.
 */
export function useSendMessage(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      input,
    }: {
      channelId: string;
      input: MessageCreateInput;
    }) =>
      apiFetch<MessageWithSender>(supabase, `messages/channels/${channelId}/messages`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { channelId }) => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMessages(tenantId, channelId),
      });
    },
  });
}

/** Edit own message body. Sets edited_at server-side. */
export function useEditMessage(supabase: SupabaseClientLike, tenantId: string, messageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MessageUpdateInput) =>
      apiFetch<MessageWithSender>(supabase, `messages/messages/${messageId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMessages(tenantId, updated.channel_id),
      });
    },
  });
}

/** Soft-delete a message (sets deleted_at). Own messages or tenant admin. */
export function useDeleteMessage(
  supabase: SupabaseClientLike,
  tenantId: string,
  messageId: string,
  channelId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(supabase, `messages/messages/${messageId}`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMessages(tenantId, channelId),
      });
    },
  });
}

/** Toggle a reaction emoji (insert if absent, delete if already present). */
export function useToggleReaction(
  supabase: SupabaseClientLike,
  tenantId: string,
  messageId: string,
  channelId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReactionToggleInput) =>
      apiFetch<{ added: boolean; reaction: Reaction | null }>(
        supabase,
        `messages/messages/${messageId}/reactions`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMessages(tenantId, channelId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Channel member hooks
// ---------------------------------------------------------------------------

/** List members of a channel. */
export function useChannelMembers(
  supabase: SupabaseClientLike,
  tenantId: string,
  channelId: string,
) {
  return useQuery({
    queryKey: messagesKeys.channelMembers(tenantId, channelId),
    queryFn: () => apiFetch<ChannelMember[]>(supabase, `messages/channels/${channelId}/members`),
    enabled: Boolean(tenantId) && Boolean(channelId),
  });
}

/** Add a member to a channel. Channel admin only (enforced server-side). */
export function useAddChannelMember(
  supabase: SupabaseClientLike,
  tenantId: string,
  channelId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChannelMemberAddInput) =>
      apiFetch<ChannelMember>(supabase, `messages/channels/${channelId}/members`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMembers(tenantId, channelId),
      });
      void qc.invalidateQueries({
        queryKey: messagesKeys.channel(tenantId, channelId),
      });
    },
  });
}

/** Remove a member from a channel. Self-removal or channel admin. */
export function useRemoveChannelMember(
  supabase: SupabaseClientLike,
  tenantId: string,
  channelId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiDelete(supabase, `messages/channels/${channelId}/members/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMembers(tenantId, channelId),
      });
      void qc.invalidateQueries({
        queryKey: messagesKeys.channel(tenantId, channelId),
      });
    },
  });
}

export interface MentionRow {
  message_id: string;
  channel_id: string;
  channel_name: string;
  sender_display_name: string | null;
  snippet: string;
  created_at: string;
  is_read: boolean;
}

/** List all unread @mentions for the current user in this tenant. */
export function useMentions(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: messagesKeys.mentions(tenantId),
    queryFn: () => apiFetch<MentionRow[]>(supabase, 'messages/mentions'),
    enabled: Boolean(tenantId),
  });
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  user_ids: string[];
  reacted_by_me: boolean;
}

/** Read aggregated reactions for a single message. */
export function useReactions(supabase: SupabaseClientLike, tenantId: string, messageId: string) {
  return useQuery({
    queryKey: messagesKeys.reactions(tenantId, messageId),
    queryFn: () =>
      apiFetch<ReactionSummary[]>(supabase, `messages/messages/${messageId}/reactions`),
    enabled: Boolean(tenantId) && Boolean(messageId),
  });
}

/** Add a reaction to a message (insert only; use useToggleReaction for toggle behaviour). */
export function useAddReaction(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      apiFetch<Reaction>(supabase, `messages/messages/${messageId}/reactions`, {
        method: 'POST',
        body: JSON.stringify({ emoji } satisfies Omit<ReactionToggleInput, 'message_id'>),
      }),
    onSuccess: (_data, { messageId }) => {
      void qc.invalidateQueries({ queryKey: messagesKeys.reactions(tenantId, messageId) });
      void qc.invalidateQueries({ queryKey: messagesKeys.channelMessages(tenantId, '') });
    },
  });
}

/** Update own last_read_at watermark (drives unread badge). */
export function useMarkLastRead(supabase: SupabaseClientLike, tenantId: string, channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ChannelMember>(supabase, `messages/channels/${channelId}/members/me/read`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagesKeys.channel(tenantId, channelId),
      });
      void qc.invalidateQueries({
        queryKey: messagesKeys.channelMembers(tenantId, channelId),
      });
    },
  });
}
